import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { listingTableSx } from '../listingTableSx';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Psychology as AIIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  Assignment as AssignmentIcon,
  Security as SecurityIcon,
  Apps as AppsIcon,
  VisibilityOutlined as ViewIcon,
} from '@mui/icons-material';
import { aiGovernanceAPI } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { ListingPagination, ListingSortSelect } from '../ListingPagination';
import { DEFAULT_LISTING_SORT, ListingSortValue, sortListing } from '../../utils/listingSort';
import { BTN_SM, CARD_SX, FIELD_SX, MENU_PROPS, PAGE, SELECT_SX, TABS_SX } from '../../constants/pageStyles';
import { UI_TOKENS } from '../../constants/uiTokens';
import type {
  AIAccessGrant,
  AIAsset,
  AIDataScopeOptions,
  AIGovernanceEnums,
  StructuredScope,
} from '../../types/aiGovernance';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { chipSxFromSurface, getLifecycleStatusSurface, getStatusSurface } from '../../utils/appearanceTokens';
import type { PaletteMode } from '@mui/material';

const TABLE_HEADER_SX = {
  fontWeight: 700,
  fontSize: UI_TOKENS.text.labelXs,
  textTransform: 'uppercase' as const,
  letterSpacing: 0.4,
  color: 'text.secondary',
  py: 1.25,
  whiteSpace: 'nowrap' as const,
  bgcolor: 'grey.50',
  borderBottom: '1px solid',
  borderColor: 'divider',
};

const GRANT_COLUMNS = [
  { label: 'Agent', width: '18%' },
  { label: 'Target App', width: '14%' },
  { label: 'Status', width: '12%' },
  { label: 'Scope', width: '14%' },
  { label: 'Security Review', width: '11%' },
  { label: 'Updated', width: '10%' },
  { label: 'Actions', width: '16%' },
];

function paginateRows<T>(items: T[], page: number, rowsPerPage: number) {
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * rowsPerPage;
  return { slice: items.slice(start, start + rowsPerPage), total, totalPages, safePage, start };
}

function isPendingDecision(status: string): boolean {
  return status === 'Requested' || status === 'Pending-Security-Review';
}

function isActiveGrant(status: string): boolean {
  return status === 'Approved' || status === 'Approved-with-conditions';
}

const AGENT_TYPES = new Set(['AI Agent', 'Copilot', 'AI Automation']);

function grantStatusStyle(status: string, mode: PaletteMode) {
  return getLifecycleStatusSurface(status, mode);
}

function formatGrantStatus(status: string): string {
  if (status === 'Pending-Security-Review') return 'Security Review';
  if (status === 'Approved-with-conditions') return 'Approved (cond.)';
  return status;
}

function statusChip(label: string, mode: PaletteMode) {
  const s = grantStatusStyle(label, mode);
  return (
    <Chip
      label={formatGrantStatus(label)}
      size="small"
      sx={{
        height: 20,
        fontSize: UI_TOKENS.text.helper,
        fontWeight: 600,
        ...chipSxFromSurface(s),
        '& .MuiChip-label': { px: 1 },
      }}
    />
  );
}

function renderDialogTitle(title: string, subtitle: string | undefined, onClose: () => void) {
  return (
    <DialogTitle
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        py: 1.5,
        px: 2.5,
        bgcolor: 'grey.50',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, minWidth: 0 }}>
        <AIIcon sx={{ color: 'primary.main', fontSize: 20, mt: 0.25 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.3 }}>{title}</Typography>
          {subtitle && (
            <Typography sx={{ fontSize: UI_TOKENS.text.helper, color: 'text.secondary', mt: 0.25 }}>{subtitle}</Typography>
          )}
        </Box>
      </Box>
      <IconButton size="small" onClick={onClose} aria-label="close" sx={{ mt: -0.25 }}>
        <CloseIcon sx={{ fontSize: '1rem' }} />
      </IconButton>
    </DialogTitle>
  );
}

function renderSectionHeader(title: string, icon?: React.ReactNode) {
  return (
    <Grid item xs={12}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, mb: 0.25 }}>
        {icon}
        <Typography sx={PAGE.sectionTitle}>{title}</Typography>
        <Divider sx={{ flex: 1 }} />
      </Box>
    </Grid>
  );
}

interface AccessDeepLink {
  grantId: string;
  appId?: string;
  panel: 'owner' | 'requests';
}

interface Props {
  assets: AIAsset[];
  enums: AIGovernanceEnums | null;
  refreshToken?: number;
  deepLink?: AccessDeepLink;
  onDeepLinkHandled?: () => void;
}

const AiGovernanceAccessPanel: React.FC<Props> = ({
  assets,
  enums,
  refreshToken = 0,
  deepLink,
  onDeepLinkHandled,
}) => {
  const { showNotification, showError } = useNotification();
  const { colors, resolvedMode } = useTheme();
  const [panelTab, setPanelTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [outbound, setOutbound] = useState<AIAccessGrant[]>([]);
  const [inbound, setInbound] = useState<AIAccessGrant[]>([]);
  const [activeGrants, setActiveGrants] = useState<AIAccessGrant[]>([]);
  const [manageableApps, setManageableApps] = useState<Array<{ id: string; name: string }>>([]);
  const [consoleAppId, setConsoleAppId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [decideOpen, setDecideOpen] = useState(false);
  const [decideReadOnly, setDecideReadOnly] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [openCampaign, setOpenCampaign] = useState<string | null>(null);
  const [campaignGrants, setCampaignGrants] = useState<AIAccessGrant[]>([]);
  const [selectedGrant, setSelectedGrant] = useState<AIAccessGrant | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [scopeOptions, setScopeOptions] = useState<AIDataScopeOptions | null>(null);
  const [decisionComment, setDecisionComment] = useState('');
  const [revokeReason, setRevokeReason] = useState('');
  const [conditionText, setConditionText] = useState('');
  const [decideScopeOptions, setDecideScopeOptions] = useState<AIDataScopeOptions | null>(null);
  const [decideScopeForm, setDecideScopeForm] = useState({
    environment: 'Dev',
    access_duration: 'Standing',
    action_scope: [] as string[],
    entitlement_ids: [] as string[],
    data_classifications: [] as string[],
  });

  const [requestForm, setRequestForm] = useState({
    requesting_agent_id: '',
    target_application_id: '',
    intent_category: 'Data Analysis',
    justification: '',
    environment: 'Dev',
    access_duration: 'Time-bound',
    standing_reason: '',
    action_scope: ['Read'] as string[],
    entitlement_ids: [] as string[],
    data_classifications: ['Internal'] as string[],
  });

  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [listingSort, setListingSort] = useState<ListingSortValue>(DEFAULT_LISTING_SORT);
  const [outboundPage, setOutboundPage] = useState(1);
  const [inboundPage, setInboundPage] = useState(1);
  const [activePage, setActivePage] = useState(1);

  const activeAgents = useMemo(
    () => assets.filter((a) => AGENT_TYPES.has(a.asset_type) && a.status === 'Active'),
    [assets]
  );

  const certificationCampaigns = useMemo(
    () =>
      Array.from(
        campaignGrants
          .filter((g) => g.pending_re_attestation && isActiveGrant(g.grant_status))
          .reduce((map, g) => {
            const owner = g.owner_asset_id || 'Unassigned owner';
            const list = map.get(owner) || [];
            list.push(g);
            map.set(owner, list);
            return map;
          }, new Map<string, AIAccessGrant[]>())
      ),
    [campaignGrants]
  );

  const loadOutbound = useCallback(async () => {
    const res = await aiGovernanceAPI.listAccessRequests();
    setOutbound(res.data.data ?? []);
  }, []);

  const loadConsole = useCallback(async (appId: string) => {
    if (!appId) {
      setInbound([]);
      setActiveGrants([]);
      return;
    }
    const [pendingRes, activeRes] = await Promise.all([
      aiGovernanceAPI.listInboundRequests(appId, 'pending'),
      aiGovernanceAPI.listApplicationGrants(appId, 'active'),
    ]);
    setInbound(pendingRes.data.data ?? []);
    setActiveGrants(activeRes.data.data ?? []);
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const manageableRes = await aiGovernanceAPI.listManageableApplications();
      const apps = manageableRes.data.data ?? [];
      setManageableApps(apps);
      await loadOutbound();
      const campaignRes = await aiGovernanceAPI.listCertificationCampaigns().catch(() => null);
      setCampaignGrants(campaignRes?.data.data ?? []);
      if (consoleAppId) {
        await loadConsole(consoleAppId);
      }
    } catch (e: unknown) {
      const msg = getApiErrorMessage(e, 'Failed to load access data');
      setError(msg);
      showError(msg);
    } finally {
      setLoading(false);
    }
  }, [consoleAppId, loadConsole, loadOutbound, showError]);

  useEffect(() => {
    void loadAll();
  }, [loadAll, refreshToken]);

  useEffect(() => {
    if (consoleAppId) {
      void loadConsole(consoleAppId);
    }
  }, [consoleAppId, loadConsole]);

  const [deepLinkHandled, setDeepLinkHandled] = useState(false);

  useEffect(() => {
    setDeepLinkHandled(false);
  }, [deepLink?.grantId]);

  useEffect(() => {
    if (!deepLink?.grantId || deepLinkHandled) return;

    let cancelled = false;

    async function openFromDeepLink() {
      setLoading(true);
      setError(null);
      try {
        if (deepLink!.panel === 'owner') {
          setPanelTab(1);
          if (deepLink!.appId) {
            setConsoleAppId(deepLink!.appId);
            await loadConsole(deepLink!.appId);
          }
        } else {
          setPanelTab(0);
          await loadOutbound();
        }

        const grantRes = await aiGovernanceAPI.getAccessGrant(deepLink!.grantId);
        const grant = grantRes.data.data;
        if (!cancelled && grant) {
          openDecideDialog(grant);
        }
      } catch {
        if (!cancelled) {
          showError('Could not open the linked access request. It may have already been processed.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setDeepLinkHandled(true);
          onDeepLinkHandled?.();
        }
      }
    }

    void openFromDeepLink();
    return () => {
      cancelled = true;
    };
    // openDecideDialog is stable enough for deep-link bootstrap
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLink, deepLinkHandled, loadConsole, loadOutbound, onDeepLinkHandled]);

  useEffect(() => {
    setOutboundPage(1);
  }, [outbound.length, rowsPerPage]);

  useEffect(() => {
    setInboundPage(1);
  }, [inbound.length, rowsPerPage, consoleAppId]);

  useEffect(() => {
    setActivePage(1);
  }, [activeGrants.length, rowsPerPage, consoleAppId]);

  const loadScopeOptions = async (appId: string) => {
    if (!appId) {
      setScopeOptions(null);
      return;
    }
    const res = await aiGovernanceAPI.getDataScopeOptions(appId);
    setScopeOptions(res.data.data ?? null);
  };

  const openSubmitDialog = () => {
    setRequestForm({
      requesting_agent_id: activeAgents[0]?.id ?? '',
      target_application_id: '',
      intent_category: 'Data Analysis',
      justification: '',
      environment: 'Dev',
      access_duration: 'Time-bound',
      standing_reason: '',
      action_scope: ['Read'],
      entitlement_ids: [],
      data_classifications: ['Internal'],
    });
    setScopeOptions(null);
    setSubmitOpen(true);
  };

  const buildScope = (): StructuredScope => ({
    schema_version: 1,
    environment: requestForm.environment,
    data_scope: {
      entitlement_ids: requestForm.entitlement_ids,
      data_classifications: requestForm.data_classifications,
      account_ids: [],
    },
    action_scope: requestForm.action_scope,
    access_duration: requestForm.access_duration,
    expires_at: requestForm.access_duration === 'Time-bound'
      ? new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString()
      : null,
  });

  const buildDecideScope = (): StructuredScope | null => {
    if (!selectedGrant) return null;
    return {
      schema_version: 1,
      environment: decideScopeForm.environment,
      data_scope: {
        entitlement_ids: decideScopeForm.entitlement_ids,
        data_classifications: decideScopeForm.data_classifications,
        account_ids: selectedGrant.requested_scope.data_scope.account_ids ?? [],
      },
      action_scope: decideScopeForm.action_scope,
      access_duration: decideScopeForm.access_duration,
      expires_at: selectedGrant.requested_scope.expires_at ?? null,
    };
  };

  const handleSubmitRequest = async () => {
    if (requestForm.access_duration === 'Standing' && !requestForm.standing_reason.trim()) {
      showError('Standing access needs a reason.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const justification = requestForm.access_duration === 'Standing'
        ? `Standing access: ${requestForm.standing_reason.trim()}\n${requestForm.justification}`.trim()
        : requestForm.justification;
      await aiGovernanceAPI.submitAccessRequest({
        requesting_agent_id: requestForm.requesting_agent_id,
        target_application_id: requestForm.target_application_id,
        requested_scope: buildScope(),
        intent_category: requestForm.intent_category,
        justification,
      });
      showNotification('Access request submitted', 'success');
      setSubmitOpen(false);
      await loadOutbound();
      if (consoleAppId) await loadConsole(consoleAppId);
    } catch (e: unknown) {
      showError(getApiErrorMessage(e, 'Failed to submit access request'));
    } finally {
      setSubmitting(false);
    }
  };

  const openDecideDialog = (grant: AIAccessGrant, readOnly = false) => {
    setSelectedGrant(grant);
    setDecideReadOnly(readOnly);
    setDecisionComment('');
    setConditionText('');
    const rs = grant.requested_scope;
    setDecideScopeForm({
      environment: rs.environment,
      access_duration: rs.access_duration ?? 'Standing',
      action_scope: [...rs.action_scope],
      entitlement_ids: [...(rs.data_scope.entitlement_ids ?? [])],
      data_classifications: [...(rs.data_scope.data_classifications ?? [])],
    });
    setDecideScopeOptions(null);
    setDecideOpen(true);
    if (!readOnly) {
      void aiGovernanceAPI
        .getDataScopeOptions(grant.target_application_id)
        .then((res) => setDecideScopeOptions(res.data.data ?? null))
        .catch(() => setDecideScopeOptions(null));
    }
  };

  const openViewDialog = (grant: AIAccessGrant) => openDecideDialog(grant, true);

  const goToOwnerConsole = (appId: string) => {
    setPanelTab(1);
    setConsoleAppId(appId);
    void loadConsole(appId);
  };

  const handleDecide = async (decision: 'approve' | 'approve-with-conditions' | 'deny') => {
    if (!selectedGrant) return;
    if (decision !== 'deny') {
      if (decideScopeForm.action_scope.length === 0) {
        showError('Granted scope must include at least one action');
        return;
      }
      if (decideScopeForm.data_classifications.length === 0) {
        showError('Granted scope must include at least one data classification');
        return;
      }
    }
    setSubmitting(true);
    try {
      await aiGovernanceAPI.decideAccessGrant(selectedGrant.id, {
        decision,
        granted_scope: decision === 'deny' ? undefined : buildDecideScope() ?? selectedGrant.requested_scope,
        comment: decisionComment,
        conditions:
          decision === 'approve-with-conditions' && conditionText.trim()
            ? [{ condition_type: 'owner_note', condition_value: conditionText.trim() }]
            : undefined,
      });
      showNotification(`Grant ${decision.replace(/-/g, ' ')}`, 'success');
      setDecideOpen(false);
      await loadOutbound();
      if (consoleAppId) await loadConsole(consoleAppId);
    } catch (e: unknown) {
      showError(getApiErrorMessage(e, 'Failed to process decision'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async () => {
    if (!selectedGrant) return;
    setSubmitting(true);
    try {
      await aiGovernanceAPI.revokeAccessGrant(selectedGrant.id, revokeReason);
      showNotification('Grant revoked', 'success');
      setRevokeOpen(false);
      await loadOutbound();
      if (consoleAppId) await loadConsole(consoleAppId);
    } catch (e: unknown) {
      showError(getApiErrorMessage(e, 'Failed to revoke grant'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecertify = async (grant: AIAccessGrant) => {
    setSubmitting(true);
    try {
      await aiGovernanceAPI.recertifyAccessGrant(grant.id);
      showNotification('Grant recertified', 'success');
      if (consoleAppId) await loadConsole(consoleAppId);
    } catch (e: unknown) {
      showError(getApiErrorMessage(e, 'Failed to recertify grant'));
    } finally {
      setSubmitting(false);
    }
  };

  const renderGrantActions = (g: AIAccessGrant, mode: 'outbound' | 'inbound' | 'active') => (
    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
      <Button
        size="small"
        variant="outlined"
        startIcon={<ViewIcon sx={{ fontSize: '0.85rem !important' }} />}
        onClick={() => openViewDialog(g)}
        sx={BTN_SM}
      >
        View
      </Button>
      {mode === 'inbound' && isPendingDecision(g.grant_status) && (
        <Button
          size="small"
          variant="contained"
          onClick={() => openDecideDialog(g)}
          sx={{ ...BTN_SM, bgcolor: colors.primary, fontWeight: 600, minWidth: 72 }}
        >
          {g.grant_status === 'Pending-Security-Review' ? 'Security Review' : 'Review'}
        </Button>
      )}
      {mode === 'outbound' && g.grant_status === 'Requested' && (
        <Button
          size="small"
          variant="outlined"
          onClick={() => goToOwnerConsole(g.target_application_id)}
          sx={BTN_SM}
        >
          Owner Console
        </Button>
      )}
      {mode === 'outbound' && g.grant_status === 'Pending-Security-Review' && (
        <Button
          size="small"
          variant="contained"
          onClick={() => openDecideDialog(g)}
          sx={{ ...BTN_SM, bgcolor: colors.primary, fontWeight: 600, minWidth: 72 }}
        >
          Security Review
        </Button>
      )}
      {(mode === 'outbound' || mode === 'active') && isActiveGrant(g.grant_status) && (
        <>
          <Button
            size="small"
            variant="outlined"
            sx={BTN_SM}
            onClick={() => void handleRecertify(g)}
            disabled={submitting}
          >
            Recertify
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            sx={BTN_SM}
            onClick={() => {
              setSelectedGrant(g);
              setRevokeReason('');
              setRevokeOpen(true);
            }}
          >
            Revoke
          </Button>
        </>
      )}
    </Box>
  );

  const renderPaginationFooter = (
    total: number,
    page: number,
    totalPages: number,
    _start: number,
    onPageChange: (p: number) => void
  ) => {
    if (total === 0) return null;
    return (
      <Box>
        <ListingPagination
          page={page}
          totalPages={totalPages}
          totalItems={total}
          pageSize={rowsPerPage}
          onPageChange={onPageChange}
          onPageSizeChange={setRowsPerPage}
          pageSizeSelectSx={SELECT_SX}
          pageSizeMenuProps={MENU_PROPS}
        />
      </Box>
    );
  };

  const renderGrantTable = (rows: AIAccessGrant[], mode: 'outbound' | 'inbound' | 'active', emptyMessage: string) => {
    const page = mode === 'outbound' ? outboundPage : mode === 'inbound' ? inboundPage : activePage;
    const setPage = mode === 'outbound' ? setOutboundPage : mode === 'inbound' ? setInboundPage : setActivePage;
    const { slice, total, totalPages, safePage, start } = paginateRows(sortListing(rows, listingSort), page, rowsPerPage);

    const campaigns =
      mode === 'active'
        ? Array.from(
            rows
              .filter((g) => g.pending_re_attestation && isActiveGrant(g.grant_status))
              .reduce((map, g) => {
                const owner = g.owner_asset_id || 'Unassigned owner';
                const list = map.get(owner) || [];
                list.push(g);
                map.set(owner, list);
                return map;
              }, new Map<string, AIAccessGrant[]>())
          )
        : [];

    return (
      <>
        {campaigns.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, fontWeight: 700, mb: 0.5 }}>
              Certification campaigns
            </Typography>
            {campaigns.map(([owner, grants]) => (
              <Box key={owner} sx={{ mb: 0.75 }}>
                <Button
                  size="small"
                  variant="text"
                  sx={{ textTransform: 'none' }}
                  onClick={() => setOpenCampaign((current) => (current === owner ? null : owner))}
                >
                  {owner}: {grants.length} grant{grants.length === 1 ? '' : 's'} to review
                </Button>
                {openCampaign === owner && (
                  <Typography sx={{ fontSize: UI_TOKENS.text.helper, color: 'text.secondary', pl: 1 }}>
                    {grants.map((g) => g.target_application_name).join(', ')}. Opening this list does not certify or revoke.
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
        <TableContainer sx={{ maxHeight: mode === 'outbound' ? 'calc(100vh - 400px)' : 'min(420px, 50vh)' }}>
          <Table stickyHeader size="small" sx={{ ...listingTableSx, tableLayout: 'fixed', width: '100%', minWidth: 960 }}>
            <TableHead>
              <TableRow>
                {GRANT_COLUMNS.map(({ label, width }) => (
                  <TableCell
                    key={label}
                    sx={{ ...TABLE_HEADER_SX, width }}
                    align={label === 'Actions' ? 'right' : 'left'}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(4)].map((_, i) => (
                  <TableRow key={i}>
                    {GRANT_COLUMNS.map((_, j) => (
                      <TableCell key={j} sx={{ py: 1 }}>
                        <Skeleton height={22} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={GRANT_COLUMNS.length} align="center" sx={{ py: 5 }}>
                    <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, color: 'text.secondary' }}>{emptyMessage}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                slice.map((g, idx) => (
                  <TableRow
                    key={g.id}
                    hover
                    sx={{
                      bgcolor: idx % 2 === 0 ? 'background.paper' : 'grey.50',
                      '&:hover': { bgcolor: 'action.hover' },
                      '&:last-child td': { borderBottom: 0 },
                    }}
                  >
                    <TableCell sx={{ py: 1 }}>
                      <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, fontWeight: 600 }} noWrap>
                        {g.agent_name}
                      </Typography>
                      <Typography
                        sx={{ fontSize: UI_TOKENS.text.helper, color: 'text.secondary', fontFamily: 'monospace' }}
                        noWrap
                      >
                        {g.agent_code} · {g.owner_asset_id || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography sx={{ fontSize: UI_TOKENS.text.bodySm }} noWrap>
                        {g.target_application_name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                        {statusChip(g.grant_status, resolvedMode)}
                        {g.pending_re_attestation && isActiveGrant(g.grant_status) && (
                          <Chip
                            label="Re-attestation"
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              ...chipSxFromSurface(getStatusSurface('warning', resolvedMode)),
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, color: 'text.secondary' }} noWrap>
                        {g.requested_scope.action_scope.join(', ')}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      {g.requires_security_review ? (
                        <Chip
                          label="Required"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: UI_TOKENS.text.helper,
                            fontWeight: 600,
                            ...chipSxFromSurface(getStatusSurface('warning', resolvedMode)),
                            '& .MuiChip-label': { px: 1 },
                          }}
                        />
                      ) : (
                        <Typography sx={{ fontSize: UI_TOKENS.text.helper, color: 'text.disabled' }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography sx={{ fontSize: UI_TOKENS.text.helper, color: 'text.secondary' }} noWrap>
                        {new Date(g.updated_at).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }} align="right">
                      {renderGrantActions(g, mode)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!loading && rows.length > 0 && renderPaginationFooter(total, safePage, totalPages, start, setPage)}
      </>
    );
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2, '& .MuiAlert-message': { fontSize: UI_TOKENS.text.bodySm } }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ ...CARD_SX, overflow: 'hidden', mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Tabs value={panelTab} onChange={(_, v) => setPanelTab(v)} sx={TABS_SX}>
            <Tab label="My Access Requests" />
            <Tab label="Owner Console" />
          </Tabs>
          <Box sx={{ display: 'flex', gap: 1, pr: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
              onClick={() => void loadAll()}
              disabled={loading}
              sx={BTN_SM}
            >
              Refresh
            </Button>
            {panelTab === 0 && (
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon sx={{ fontSize: '0.9rem !important' }} />}
                disabled={activeAgents.length === 0}
                onClick={openSubmitDialog}
                sx={{ ...BTN_SM, bgcolor: colors.primary, fontWeight: 600, '&:hover': { bgcolor: colors.dark } }}
              >
                New Request
              </Button>
            )}
          </Box>
        </Box>

        {panelTab === 0 ? (
          <>
            <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
              <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, fontWeight: 600, color: 'text.secondary' }}>
                {loading ? 'Loading…' : `${outbound.length} outbound request${outbound.length !== 1 ? 's' : ''}`}
              </Typography>
              <ListingSortSelect
                value={listingSort}
                onChange={setListingSort}
                sx={SELECT_SX}
                menuProps={MENU_PROPS}
              />
            </Box>
            {activeAgents.length === 0 && !loading && (
              <Alert severity="info" sx={{ m: 2, '& .MuiAlert-message': { fontSize: UI_TOKENS.text.bodySm } }}>
                Activate an AI Agent, Copilot, or Automation asset before submitting cross-application access requests.
              </Alert>
            )}
            {renderGrantTable(outbound, 'outbound', 'No access requests yet. Submit a new cross-app request to get started.')}
          </>
        ) : (
          <>
            <Paper variant="outlined" sx={{ ...CARD_SX, m: 2, p: 2, borderRadius: 1.5, borderColor: 'grey.200' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
                <FilterListIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                <Typography sx={PAGE.sectionTitle}>Application Context</Typography>
              </Box>
              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small" sx={SELECT_SX}>
                    <InputLabel>Target Application</InputLabel>
                    <Select
                      label="Target Application"
                      value={consoleAppId}
                      onChange={(e) => setConsoleAppId(e.target.value)}
                      MenuProps={MENU_PROPS}
                    >
                      <MenuItem value="">
                        <em>Select an application…</em>
                      </MenuItem>
                      {manageableApps.map((app) => (
                        <MenuItem key={app.id} value={app.id}>
                          {app.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography sx={PAGE.helper}>
                    Review inbound agent access requests and manage active grants for applications you own.
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {certificationCampaigns.length > 0 && (
              <Box sx={{ px: 2, pb: 1 }}>
                <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, fontWeight: 700, mb: 0.5 }}>
                  Certification campaigns
                </Typography>
                <Typography sx={{ fontSize: UI_TOKENS.text.helper, color: 'text.secondary', mb: 0.5 }}>
                  One list per owner. Opening a list does not certify or revoke.
                </Typography>
                {certificationCampaigns.map(([owner, grants]) => (
                  <Box key={owner} sx={{ mb: 0.75 }}>
                    <Button
                      size="small"
                      variant="text"
                      sx={{ textTransform: 'none' }}
                      onClick={() => setOpenCampaign((current) => (current === owner ? null : owner))}
                    >
                      {owner}: {grants.length} grant{grants.length === 1 ? '' : 's'} to review
                    </Button>
                    {openCampaign === owner && (
                      <Typography sx={{ fontSize: UI_TOKENS.text.helper, color: 'text.secondary', pl: 1 }}>
                        {grants.map((g) => g.target_application_name).join(', ')}.
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}

            {manageableApps.length === 0 && !loading && (
              <Alert severity="warning" sx={{ m: 2, '& .MuiAlert-message': { fontSize: UI_TOKENS.text.bodySm } }}>
                You do not have owner or delegate access to any applications. Contact an application owner to review
                inbound AI access requests.
              </Alert>
            )}

            <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, fontWeight: 600, color: 'text.secondary' }}>
                Pending inbound requests ({inbound.length})
              </Typography>
            </Box>
            {consoleAppId || inbound.length > 0 ? (
              renderGrantTable(inbound, 'inbound', 'No pending requests for this application.')
            ) : (
              <Box sx={{ py: 5, textAlign: 'center' }}>
                <AppsIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
                <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, color: 'text.secondary' }}>
                  Select an application to view pending requests
                </Typography>
              </Box>
            )}

            <Box sx={{ px: 2, py: 1.25, borderTop: '1px solid', borderColor: 'divider', borderBottom: '1px solid' }}>
              <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, fontWeight: 600, color: 'text.secondary' }}>
                Active grants ({activeGrants.length})
              </Typography>
            </Box>
            {consoleAppId ? (
              renderGrantTable(activeGrants, 'active', 'No active grants for this application.')
            ) : (
              <Box sx={{ py: 5, textAlign: 'center' }}>
                <SecurityIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
                <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, color: 'text.secondary' }}>
                  Select an application to view active grants
                </Typography>
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Submit request dialog */}
      <Dialog open={submitOpen} onClose={() => setSubmitOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        {renderDialogTitle('New Cross-App Access Request', 'Structured scope for agent access to another application', () => setSubmitOpen(false))}
        <DialogContent sx={{ pt: 2, px: 2.5 }}>
          <Grid container spacing={1.5}>
            {renderSectionHeader('Agent & Target', <AssignmentIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />)}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required size="small" sx={SELECT_SX}>
                <InputLabel>Requesting Agent</InputLabel>
                <Select
                  label="Requesting Agent"
                  value={requestForm.requesting_agent_id}
                  onChange={(e) => setRequestForm({ ...requestForm, requesting_agent_id: e.target.value })}
                  MenuProps={MENU_PROPS}
                >
                  {activeAgents.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.name} ({a.asset_code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required size="small" sx={SELECT_SX}>
                <InputLabel>Target Application</InputLabel>
                <Select
                  label="Target Application"
                  value={requestForm.target_application_id}
                  onChange={(e) => {
                    const id = e.target.value;
                    setRequestForm({ ...requestForm, target_application_id: id, entitlement_ids: [] });
                    void loadScopeOptions(id);
                  }}
                  MenuProps={MENU_PROPS}
                >
                  {manageableApps
                    .filter((app) => {
                      const agent = activeAgents.find((a) => a.id === requestForm.requesting_agent_id);
                      return agent ? app.id !== agent.linked_application_id : true;
                    })
                    .map((app) => (
                      <MenuItem key={app.id} value={app.id}>
                        {app.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small" sx={SELECT_SX}>
                <InputLabel>Intent Category</InputLabel>
                <Select
                  label="Intent Category"
                  value={requestForm.intent_category}
                  onChange={(e) => setRequestForm({ ...requestForm, intent_category: e.target.value })}
                  MenuProps={MENU_PROPS}
                >
                  {(enums?.intent_categories ?? ['Data Analysis']).map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small" sx={SELECT_SX}>
                <InputLabel>Environment</InputLabel>
                <Select
                  label="Environment"
                  value={requestForm.environment}
                  onChange={(e) => setRequestForm({ ...requestForm, environment: e.target.value })}
                  MenuProps={MENU_PROPS}
                >
                  {(enums?.environments ?? ['Dev']).map((env) => (
                    <MenuItem key={env} value={env}>
                      {env}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small" sx={SELECT_SX}>
                <InputLabel>Access Duration</InputLabel>
                <Select
                  label="Access Duration"
                  value={requestForm.access_duration}
                  onChange={(e) => setRequestForm({ ...requestForm, access_duration: e.target.value })}
                  MenuProps={MENU_PROPS}
                >
                  <MenuItem value="Time-bound">90 days</MenuItem>
                  <MenuItem value="Standing">Standing</MenuItem>
                </Select>
              </FormControl>
              <Typography sx={{ ...PAGE.helper, mt: 0.5 }}>
                New access ends in 90 days. Standing access stays until someone revokes it.
              </Typography>
            </Grid>
            {requestForm.access_duration === 'Standing' && (
              <Grid item xs={12}>
                <TextField
                  label="Reason for standing access"
                  required
                  fullWidth
                  size="small"
                  value={requestForm.standing_reason}
                  onChange={(e) => setRequestForm({ ...requestForm, standing_reason: e.target.value })}
                  sx={FIELD_SX}
                />
              </Grid>
            )}

            {renderSectionHeader('Scope', <SecurityIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />)}
            <Grid item xs={12}>
              <Typography sx={{ ...PAGE.subsectionTitle, mb: 0.75 }}>Action Scope</Typography>
              <FormGroup row>
                {(enums?.action_scopes ?? ['Read', 'Write']).map((action) => (
                  <FormControlLabel
                    key={action}
                    control={
                      <Checkbox
                        size="small"
                        checked={requestForm.action_scope.includes(action)}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...requestForm.action_scope, action]
                            : requestForm.action_scope.filter((a) => a !== action);
                          setRequestForm({ ...requestForm, action_scope: next });
                        }}
                      />
                    }
                    label={action}
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: UI_TOKENS.text.bodySm } }}
                  />
                ))}
              </FormGroup>
            </Grid>
            {scopeOptions && (
              <Grid item xs={12}>
                <Typography sx={{ ...PAGE.subsectionTitle, mb: 0.75 }}>Data Scope — Entitlements</Typography>
                <FormGroup>
                  {scopeOptions.entitlements.length === 0 ? (
                    <Typography sx={PAGE.helper}>
                      No entitlements on this application — select data classifications below.
                    </Typography>
                  ) : (
                    scopeOptions.entitlements.map((ent) => (
                      <FormControlLabel
                        key={ent.id}
                        control={
                          <Checkbox
                            size="small"
                            checked={requestForm.entitlement_ids.includes(ent.id)}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...requestForm.entitlement_ids, ent.id]
                                : requestForm.entitlement_ids.filter((id) => id !== ent.id);
                              setRequestForm({ ...requestForm, entitlement_ids: next });
                            }}
                          />
                        }
                        label={`${ent.name}${ent.risk_level ? ` (${ent.risk_level})` : ''}`}
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: UI_TOKENS.text.bodySm } }}
                      />
                    ))
                  )}
                </FormGroup>
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography sx={{ ...PAGE.subsectionTitle, mb: 0.75 }}>Data Classifications</Typography>
              <FormGroup row>
                {(enums?.data_classifications ?? ['Internal']).map((dc) => (
                  <FormControlLabel
                    key={dc}
                    control={
                      <Checkbox
                        size="small"
                        checked={requestForm.data_classifications.includes(dc)}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...requestForm.data_classifications, dc]
                            : requestForm.data_classifications.filter((d) => d !== dc);
                          setRequestForm({ ...requestForm, data_classifications: next });
                        }}
                      />
                    }
                    label={dc}
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: UI_TOKENS.text.bodySm } }}
                  />
                ))}
              </FormGroup>
            </Grid>

            {renderSectionHeader('Justification', <AssignmentIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />)}
            <Grid item xs={12}>
              <TextField
                label="Business Justification"
                required
                fullWidth
                size="small"
                multiline
                minRows={3}
                value={requestForm.justification}
                onChange={(e) => setRequestForm({ ...requestForm, justification: e.target.value })}
                sx={FIELD_SX}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button size="small" onClick={() => setSubmitOpen(false)} disabled={submitting} sx={BTN_SM}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={submitting}
            onClick={() => void handleSubmitRequest()}
            sx={{ ...BTN_SM, bgcolor: colors.primary, fontWeight: 600, minWidth: 120 }}
          >
            {submitting ? <CircularProgress size={16} color="inherit" /> : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Decide dialog */}
      <Dialog open={decideOpen} onClose={() => setDecideOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        {renderDialogTitle(
          decideReadOnly ? 'Access Request Details' : 'Review Access Request',
          selectedGrant ? `${selectedGrant.agent_name} → ${selectedGrant.target_application_name}` : undefined,
          () => setDecideOpen(false)
        )}
        <DialogContent sx={{ pt: 2, px: 2.5 }}>
          {selectedGrant && (
            <Grid container spacing={1.5}>
              {decideReadOnly && selectedGrant.decision_comment && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ '& .MuiAlert-message': { fontSize: UI_TOKENS.text.bodySm } }}>
                    {selectedGrant.decision_comment}
                  </Alert>
                </Grid>
              )}
              <Grid item xs={6}>
                <Typography sx={{ fontSize: UI_TOKENS.text.labelXs, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.25 }}>
                  Status
                </Typography>
                {statusChip(selectedGrant.grant_status, resolvedMode)}
              </Grid>
              <Grid item xs={6}>
                <Typography sx={{ fontSize: UI_TOKENS.text.labelXs, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.25 }}>
                  Environment
                </Typography>
                <Typography sx={{ fontSize: UI_TOKENS.text.bodySm }}>{selectedGrant.requested_scope.environment}</Typography>
              </Grid>

              {renderSectionHeader('Granted Scope', <SecurityIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />)}
              <Grid item xs={12}>
                <Typography sx={{ fontSize: UI_TOKENS.text.helper, color: 'text.secondary', mb: 1 }}>
                  Uncheck items to approve a narrower scope than requested. Granted scope must be a subset of the request.
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ ...PAGE.subsectionTitle, mb: 0.75 }}>Actions</Typography>
                <FormGroup>
                  {selectedGrant.requested_scope.action_scope.map((action) => (
                    <FormControlLabel
                      key={action}
                      control={
                        <Checkbox
                          size="small"
                          checked={decideScopeForm.action_scope.includes(action)}
                          disabled={decideReadOnly}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...decideScopeForm.action_scope, action]
                              : decideScopeForm.action_scope.filter((a) => a !== action);
                            setDecideScopeForm({ ...decideScopeForm, action_scope: next });
                          }}
                        />
                      }
                      label={action}
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: UI_TOKENS.text.bodySm } }}
                    />
                  ))}
                </FormGroup>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" sx={FIELD_SX}>
                  <InputLabel>Access Duration</InputLabel>
                  <Select
                    label="Access Duration"
                    value={decideScopeForm.access_duration}
                    disabled={decideReadOnly}
                    onChange={(e) => setDecideScopeForm({ ...decideScopeForm, access_duration: e.target.value })}
                    sx={SELECT_SX}
                    MenuProps={MENU_PROPS}
                  >
                    {(enums?.access_durations ?? ['Standing', 'Time-bound']).map((d) => (
                      <MenuItem key={d} value={d} disabled={d !== selectedGrant.requested_scope.access_duration && selectedGrant.requested_scope.access_duration === 'Standing'}>
                        {d}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {(selectedGrant.requested_scope.data_scope.entitlement_ids?.length ?? 0) > 0 && (
                <Grid item xs={12}>
                  <Typography sx={{ ...PAGE.subsectionTitle, mb: 0.75 }}>Entitlements</Typography>
                  <FormGroup>
                    {(decideScopeOptions?.entitlements ?? [])
                      .filter((ent) => selectedGrant.requested_scope.data_scope.entitlement_ids?.includes(ent.id))
                      .map((ent) => (
                        <FormControlLabel
                          key={ent.id}
                          control={
                            <Checkbox
                              size="small"
                              checked={decideScopeForm.entitlement_ids.includes(ent.id)}
                              disabled={decideReadOnly}
                              onChange={(e) => {
                                const next = e.target.checked
                                  ? [...decideScopeForm.entitlement_ids, ent.id]
                                  : decideScopeForm.entitlement_ids.filter((id) => id !== ent.id);
                                setDecideScopeForm({ ...decideScopeForm, entitlement_ids: next });
                              }}
                            />
                          }
                          label={`${ent.name}${ent.risk_level ? ` (${ent.risk_level})` : ''}`}
                          sx={{ '& .MuiFormControlLabel-label': { fontSize: UI_TOKENS.text.bodySm } }}
                        />
                      ))}
                  </FormGroup>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography sx={{ ...PAGE.subsectionTitle, mb: 0.75 }}>Data Classifications</Typography>
                <FormGroup row>
                  {(selectedGrant.requested_scope.data_scope.data_classifications ?? []).map((dc) => (
                    <FormControlLabel
                      key={dc}
                      control={
                        <Checkbox
                          size="small"
                          checked={decideScopeForm.data_classifications.includes(dc)}
                          disabled={decideReadOnly}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...decideScopeForm.data_classifications, dc]
                              : decideScopeForm.data_classifications.filter((d) => d !== dc);
                            setDecideScopeForm({ ...decideScopeForm, data_classifications: next });
                          }}
                        />
                      }
                      label={dc}
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: UI_TOKENS.text.bodySm } }}
                    />
                  ))}
                </FormGroup>
              </Grid>

              {!decideReadOnly && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      label="Comment"
                      fullWidth
                      size="small"
                      multiline
                      minRows={2}
                      value={decisionComment}
                      onChange={(e) => setDecisionComment(e.target.value)}
                      sx={FIELD_SX}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Conditions (optional)"
                      fullWidth
                      size="small"
                      multiline
                      minRows={2}
                      value={conditionText}
                      onChange={(e) => setConditionText(e.target.value)}
                      helperText="Applied when approving with conditions"
                      sx={FIELD_SX}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid', borderColor: 'divider', flexWrap: 'wrap', gap: 0.5 }}>
          <Button size="small" onClick={() => setDecideOpen(false)} disabled={submitting} sx={BTN_SM}>
            {decideReadOnly ? 'Close' : 'Cancel'}
          </Button>
          {!decideReadOnly && (
            <>
              <Box sx={{ flex: 1 }} />
              <Button size="small" color="error" variant="outlined" onClick={() => void handleDecide('deny')} disabled={submitting} sx={BTN_SM}>
                Deny
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => void handleDecide('approve-with-conditions')}
                disabled={submitting}
                sx={BTN_SM}
              >
                Approve with Conditions
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => void handleDecide('approve')}
                disabled={submitting}
                sx={{ ...BTN_SM, bgcolor: colors.primary, fontWeight: 600 }}
              >
                Approve
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Revoke dialog */}
      <Dialog open={revokeOpen} onClose={() => setRevokeOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        {renderDialogTitle('Revoke Grant', selectedGrant?.agent_name, () => setRevokeOpen(false))}
        <DialogContent sx={{ pt: 2, px: 2.5 }}>
          <TextField
            label="Reason"
            required
            fullWidth
            size="small"
            multiline
            minRows={2}
            value={revokeReason}
            onChange={(e) => setRevokeReason(e.target.value)}
            sx={FIELD_SX}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button size="small" onClick={() => setRevokeOpen(false)} disabled={submitting} sx={BTN_SM}>
            Cancel
          </Button>
          <Button size="small" color="error" variant="contained" onClick={() => void handleRevoke()} disabled={submitting} sx={{ ...BTN_SM, fontWeight: 600 }}>
            Revoke Grant
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AiGovernanceAccessPanel;
